<modal-dialog #newApiKeyModal [dialogTemplate]="newApiKeyDialogTemplate"></modal-dialog>
<ng-template #newApiKeyDialogTemplate>
  <div class="light-content-panel light-control-host" (keydown.escape)="close()">
    <div class="modal-header">
      <h2 class="panel-title margin-left">Create New API Key</h2>
      <button type="button" class="modal-close-btn" (click)="close()">
        <svg lucideX></svg>
      </button>
    </div>
    <form [formGroup]="apiKeyForm" (ngSubmit)="onSave()" (keydown.enter)="$event.preventDefault()">
      <div class="modal-body">
        <!-- API Key Information Section -->
        <div class="form-section">
          <div class="role-information-body">
            <div class="form-row">
              <strong>API Key Name <span class="required">*</span></strong>
              <input
                type="text"
                formControlName="name"
                class="arpio-input full-width"
                placeholder="Enter API key name (e.g., 'Production Automation Key')"
              />
              @if (nameErrors) {
                <div class="input-error-text">
                  {{ nameErrors }}
                </div>
              }
            </div>

            <label class="labeled-checkbox">
              <input type="checkbox" formControlName="enabled" class="arpio-checkbox" />
              <span class="labeled-checkbox-content">
                <span class="labeled-checkbox-title">Enable this API key immediately</span>
                <span class="modal-subtitle">
                  Disabled API keys cannot be used to access the Arpio API. You can enable or disable keys at any time.
                </span>
              </span>
            </label>
          </div>
        </div>
        <hr />
        <!-- Role Assignment Section -->
        <div class="form-section">
          <h3>Assign Roles</h3>
          <div class="table-container">
            <div class="flex-row items-center gap-4 margin-bottom">
              <div>
                <h5>Select Roles for this API Key</h5>
                <div class="input-help-text text-xs margin-top">
                  Roles determine what permissions this API key has. You can assign multiple roles.
                </div>
              </div>
              <div class="filter-container">
                @if (isModalOpen()) {
                  <input
                    pInputText
                    type="text"
                    #rolesFilter
                    (input)="filterRoles($event)"
                    placeholder="Filter roles..."
                    class="arpio-input filter-input"
                  />
                }
              </div>
            </div>
            @if (isModalOpen()) {
              <p-table
                #rolesTable
                [value]="availableRoles"
                [globalFilterFields]="['name', 'accessLevel']"
                dataKey="id"
                [scrollable]="true"
                scrollHeight="300px"
                [tableStyle]="{ 'min-width': '35rem' }"
                class="p-datatable-sm"
                [loading]="loadingRoles"
                [customSort]="true"
                (sortFunction)="customSort($event)"
              >
                <ng-template #header>
                  <tr>
                    <th scope="col" class="checkbox-column">
                      <input
                        type="checkbox"
                        class="arpio-checkbox"
                        [checked]="areAllRolesSelected()"
                        [indeterminate]="areSomeRolesSelected()"
                        (change)="toggleAllRoles($event)"
                      />
                    </th>
                    <th scope="col" pSortableColumn="name" class="role-name-column">
                      Role Name <p-sort-icon field="name"></p-sort-icon>
                    </th>
                    <th scope="col" pSortableColumn="accessLevel">
                      Access Level(s) <p-sort-icon field="accessLevel"></p-sort-icon>
                    </th>
                  </tr>
                </ng-template>
                <ng-template #body let-role>
                  <tr [ngClass]="{ 'default-role-row': isDefaultAccountRole(role) }">
                    <td class="checkbox-column">
                      <input
                        type="checkbox"
                        class="arpio-checkbox"
                        [checked]="isRoleSelected(role)"
                        (change)="toggleRole(role, $event)"
                      />
                    </td>
                    <td class="role-name-column">
                      <div>
                        {{ role.name }}
                      </div>
                    </td>
                    <td>
                      @if (role.accessLevels && role.accessLevels.length > 0) {
                        <div class="access-level-badges">
                          @for (accessLevelInfo of role.accessLevels; track accessLevelInfo) {
                            <p-tag
                              [severity]="
                                accessLevelInfo.accessLevel.includes('admin')
                                  ? 'danger'
                                  : accessLevelInfo.accessLevel.includes('read')
                                    ? 'warning'
                                    : 'info'
                              "
                              class="access-level-badge fit-width"
                              [pTooltip]="getAccessLevelTooltip(accessLevelInfo)"
                              tabindex="0"
                              role="note"
                              [attr.aria-label]="getAccessLevelTooltip(accessLevelInfo)"
                              tooltipStyleClass="custom-scrollable-tooltip badge-tooltip"
                              tooltipPosition="top"
                              [autoHide]="false"
                              [hideDelay]="200"
                              [escape]="false"
                              tooltipEvent="hover"
                              value="{{ accessLevelService.getAccessLevelDisplayName(accessLevelInfo.accessLevel) }}"
                            >
                            </p-tag>
                          }
                        </div>
                      } @else {
                        <div>{{ accessLevelService.getAccessLevelDisplayName(role.accessLevel) }}</div>
                      }
                    </td>
                  </tr>
                </ng-template>
                <ng-template #emptymessage>
                  <tr>
                    <td colspan="3" class="empty-state-cell text-center">
                      <div class="empty-state-content">
                        @if (
                          rolesTable.filteredValue && rolesTable.filteredValue.length === 0 && availableRoles.length > 0
                        ) {
                          <p class="empty-state-message">No roles match your filter.</p>
                          <p class="empty-state-submessage">
                            Try adjusting your filter criteria or clear the filter to see all roles.
                          </p>
                        }
                        @if (!rolesTable.filteredValue || availableRoles.length === 0) {
                          <p class="empty-state-message">No roles available.</p>
                          <p class="empty-state-submessage">
                            Create roles in Role Management to assign them to API keys.
                          </p>
                        }
                      </div>
                    </td>
                  </tr>
                </ng-template>
                <ng-template #summary>
                  @if (!loadingRoles) {
                    <div class="flex align-items-center justify-content-between">
                      <span
                        >Displaying {{ rolesTable.filteredValue?.length ?? rolesTable.value?.length }} of
                        {{ rolesTable.value?.length }} roles</span
                      >
                    </div>
                  }
                </ng-template>
              </p-table>
            }
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button type="button" class="arpio-secondary-button" (click)="close()">Cancel</button>
        <button type="submit" class="arpio-button" [disabled]="!isFormValid || saving">
          @if (saving) {
            <svg lucideLoaderCircle class="lucide-spin"></svg>
          }
          {{ getSaveButtonText() }}
        </button>
      </div>
    </form>
  </div>
</ng-template>

<!-- Secret Display Dialog -->
<modal-dialog #secretDialog [dialogTemplate]="secretDialogTemplate"></modal-dialog>
<ng-template #secretDialogTemplate>
  <div class="light-content-panel light-control-host api-key-secret-dialog">
    <div class="modal-header">
      <h2 class="panel-title margin-left">API Key Created Successfully</h2>
      <button type="button" class="modal-close-btn" (click)="closeSecretDialog()">
        <svg lucideX></svg>
      </button>
    </div>
    <div class="modal-body">
      <div class="secret-display-section">
        <div class="form-row margin-top">
          <strong>API Key Name</strong>
          <div class="secret-value">{{ createdApiKey?.name }}</div>
        </div>

        <div class="form-row margin-top">
          <strong>API Key ID</strong>
          <div class="secret-value monospace">{{ createdApiKey?.apiKeyId }}</div>
        </div>

        <div class="form-row margin-top">
          <div class="notice-warning margin-bottom">
            <svg lucideCircleAlert></svg>
            <p>
              <strong>Important:</strong> This is the only time you will be able to view the
              <strong>API key secret.</strong> Please copy it now and store it securely.
            </p>
          </div>
          <strong>API Key Secret</strong>
          <div class="secret-container flex items-center gap-2">
            <div class="secret-value monospace selectable">{{ createdApiKey?.secret }}</div>
            <a
              type="button"
              class="copy-button"
              (click)="copySecretToClipboard()"
              [title]="copyFeedback.copiedStates['secret'] ? 'Copied' : 'Copy to clipboard'"
            >
              @if (copyFeedback.copiedStates['secret']) {
                <svg lucideCheck class="text-success"></svg>
              } @else {
                <svg lucideCopy></svg>
              }
            </a>
          </div>
          <div class="input-help-text text-xs margin-top">
            Use this secret value when making API calls. Store it securely - you won't be able to retrieve it again.
          </div>
        </div>
      </div>
    </div>

    <div class="modal-footer">
      <button type="button" class="arpio-button" (click)="closeSecretDialog()">I've Saved the Secret</button>
    </div>
  </div>
</ng-template>
